import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Tag, Users, FileText, CalendarDays, Clock, Repeat, IndianRupee,
  Activity, Ban, CheckCircle2, Trash2, Eye, Loader2, WifiOff, Printer,
  ShieldAlert, StickyNote, Plus, FolderKanban, Bug, Mail, Hash, User as UserIcon, UserCog, UserCheck,
} from "lucide-react";
import Modal from "@/components/dashboard/Modal";
import LoadError from "@/components/dashboard/LoadError";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import {
  getCompany, getCompanySubscription, getCompanyInvoices, getPlans, changeCompanyPlan,
  cancelCompanySubscription, suspendCompany, softDeleteCompany, hardDeleteCompany, impersonateCompany,
} from "@/api/admin";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { toastError, toastSuccess } from "@/lib/toast";
import { formatDate, formatDateTime } from "@/lib/dates";
import { invoiceNumber } from "@/lib/format";

// Fallback labels only — used when the API hasn't supplied a `plan_name` (the
// offline fixture, or a plan retired from the catalog). The live name comes from
// the API, which now resolves it from the editable catalog rather than a frozen
// copy, so renaming a plan reaches this page.
const PLAN_NAME_FALLBACK = { free_trial: "Free Trial", starter: "Starter Plan", growth: "Growth Plan", custom: "Custom Plan" };

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "subscription", label: "Subscription" },
  { key: "invoices", label: "Invoices" },
  { key: "notes", label: "Notes" },
  { key: "danger", label: "Danger Zone" },
];

const NOTE_MAX_LEN = 2000;

// ---- formatters ------------------------------------------------------------
const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(Number(n) || 0);
const fmtDate = (v) => formatDate(v);
const fmtDateTime = (v) => formatDateTime(v);
function relTime(v) {
  if (!v) return "—";
  const d = new Date(v).getTime();
  if (Number.isNaN(d)) return "—";
  const s = (Date.now() - d) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  trialing: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  canceled: "bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300",
  past_due: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  refunded: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  failed: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};
function StatusBadge({ status, dot = true }) {
  const k = String(status || "").toLowerCase();
  return (
    <Badge className={cn("uppercase", STATUS_STYLE[k] || STATUS_STYLE.canceled)}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />} {k || "—"}
    </Badge>
  );
}

// Placeholder identities only. This fixture used to carry a real developer's
// name and personal Gmail address, which rendered on screen whenever the backend
// was unreachable.
const DEMO_DETAIL = {
  id: "demo", name: "Acme Inc.", slug: "acme", billing_email: "owner@acme.com", is_active: true,
  model_preset: "balanced", created_at: "2026-05-28T09:57:00Z",
  owner: { name: "Alex Owner", email: "owner@acme.com" },
  seats: 24, total_users: 26, runs: 28, projects: 6, bugs: 9,
  plan_id: "growth", plan_name: "Growth Plan", status: "active", amount_paid: 3499,
  trial_end: "2026-06-26T00:00:00Z", current_period_end: "2026-07-10T00:00:00Z",
  subscription: { plan_id: "growth", plan_name: "Growth Plan", status: "active", amount_paid: 3499, created_at: "2026-05-28T09:57:00Z", current_period_end: "2026-07-10T00:00:00Z" },
};
const DEMO_INVOICES = [
  { id: "cmq7pjg1x9", plan_id: "growth", amount: 3499, currency: "INR", status: "paid", period_start: "2026-06-10T00:00:00Z", period_end: "2026-07-10T00:00:00Z", created_at: "2026-06-11T00:00:00Z" },
  { id: "cmq7pide42", plan_id: "starter", amount: 500, currency: "INR", status: "paid", period_start: "2026-06-10T00:00:00Z", period_end: "2026-07-10T00:00:00Z", created_at: "2026-06-11T00:00:00Z" },
  { id: "cmppbk8nzz", plan_id: "growth", amount: 3499, currency: "INR", status: "refunded", period_start: "2026-05-28T00:00:00Z", period_end: "2026-06-27T00:00:00Z", created_at: "2026-05-29T00:00:00Z" },
];

// ===========================================================================
export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [impersonating, setImpersonating] = useState(false);
  const [confirmImpersonate, setConfirmImpersonate] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams(t === "overview" ? {} : { tab: t }, { replace: true });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demo, setDemo] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getCompany(id);
      setData(d);
      setDemo(false);
    } catch (e) {
      // Only a real 404 means "no such company" — everything else (500, 403, the
      // client's non-JSON guard) has to say so, or a live tenant reads as deleted.
      if (e && e.status === 0) { setData({ ...DEMO_DETAIL, id }); setDemo(true); }
      else if (e && e.status === 404) { setData(null); }
      else { setError((e && e.message) || "Couldn't load this company."); }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Impersonation is CONFIRMED, and the operator's own session is KEPT.
  // It used to be one unguarded click that overwrote their token via
  // loginWithToken() — no banner said so anywhere in the app, and the only way
  // back was signing out and in again.
  async function impersonate() {
    setImpersonating(true);
    try {
      const res = await impersonateCompany(id);
      await startImpersonation(res.access_token, {
        ...(res.impersonating || {}),
        company_name: res.impersonating?.company_name || data?.name,
      });
      // The impersonated account is the owner when there is one, but the backend
      // falls back to any active member — so land on THAT role's home, not a
      // hardcoded /admin-co the guard would bounce.
      navigate(homeForRole(res.impersonating?.role || "admin"), { replace: true });
    } catch (e) {
      toastError((e && e.message) || "Couldn't impersonate this company.");
    } finally {
      setImpersonating(false);
      setConfirmImpersonate(false);
    }
  }

  if (loading && !data) {
    return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>;
  }
  if (!data) {
    return (
      <div className="py-20 text-center">
        {error
          ? <LoadError message={error} onRetry={load} className="mx-auto max-w-xl text-left" />
          : <p className="text-slate-500 dark:text-slate-400">Company not found.</p>}
        <Link to="/admin/companies" className="mt-3 inline-block text-sm font-semibold text-brand-600">← Back to companies</Link>
      </div>
    );
  }

  const initials = (data.name || "?").slice(0, 2).toUpperCase();

  return (
    <div>
      <Link to="/admin/companies" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-600 shadow-soft transition-colors hover:text-brand-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
        <ArrowLeft size={15} /> Companies
      </Link>

      {demo && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      {/* a refresh failed — what's on screen is stale, not current */}
      <LoadError message={error} onRetry={load} className="mt-4" />

      {/* header */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 flex items-center gap-4">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-lg font-bold text-white shadow-glow">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">{data.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-400">
            <span className="font-mono">{data.slug}</span>
            <span>·</span>
            {data.is_active ? <StatusBadge status="active" /> : <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">suspended</Badge>}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setConfirmImpersonate(true)} disabled={impersonating || !data.is_active}>
          {impersonating ? <Loader2 size={15} className="animate-spin" /> : <UserCog size={15} />} Impersonate
        </Button>
      </motion.div>

      {/* tabs */}
      {/* overflow-y-hidden alongside overflow-x-auto: per the CSS overflow
          spec, setting only one axis to `auto` implicitly turns the other
          (left at `visible`) into `auto` too — so the slightest vertical
          overflow from this row's own content (the active-tab underline,
          padding rounding) could pop up a real, if pointless, vertical
          scrollbar on a row that only ever needs to scroll sideways. */}
      <div className="mt-6 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-slate-200 dark:border-white/10">
        {TABS.map((t) => {
          const active = tab === t.key;
          const danger = t.key === "danger";
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "relative whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors",
                active
                  ? danger ? "text-rose-600 dark:text-rose-400" : "text-brand-600 dark:text-brand-400"
                  : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
              )}
            >
              {t.label}
              {active && (
                <motion.span
                  layoutId="company-tab"
                  className={cn("absolute inset-x-2 -bottom-px h-0.5 rounded-full", danger ? "bg-rose-500" : "bg-brand-500")}
                  transition={{ type: "spring", stiffness: 450, damping: 36 }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* tab content */}
      <div className="mt-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            {tab === "overview" && <OverviewPanel data={data} />}
            {tab === "subscription" && <SubscriptionPanel data={data} onChanged={load} />}
            {tab === "invoices" && <InvoicesPanel id={id} demo={demo} />}
            {tab === "notes" && <NotesPanel id={id} />}
            {tab === "danger" && <DangerPanel data={data} onChanged={load} onDeleted={() => navigate("/admin/companies")} />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Confirmed, and explicit about what it does. This was a bare click that
          silently swapped the operator's session for the customer's. */}
      <Modal
        open={confirmImpersonate}
        onClose={() => (impersonating ? null : setConfirmImpersonate(false))}
        title="Sign in as this company?"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmImpersonate(false)} disabled={impersonating}>
              Cancel
            </Button>
            <Button size="sm" onClick={impersonate} disabled={impersonating}>
              {impersonating ? <Loader2 size={15} className="animate-spin" /> : <UserCheck size={15} />}
              View as this company
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
          <p>
            You&apos;ll be signed in as <strong>{data.owner?.email || data.billing_email || "the account owner"}</strong>
            {" "}and see exactly what they see.
          </p>
          <ul className="space-y-1.5 rounded-xl border border-slate-200 p-3 text-xs dark:border-white/10">
            <li>• Your own session is <strong>kept</strong> — a bar at the top of every page returns you here.</li>
            <li>• Everything you do is recorded against <strong>their</strong> account, tagged with your name.</li>
            <li>• Changing their password or email is blocked while you&apos;re viewing as them.</li>
            <li>• The session expires on its own after 30 minutes.</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
}

// ---- shared tile -----------------------------------------------------------
function Tile({ icon: Icon, label, value, delay = 0, accent = "brand" }) {
  const tints = {
    brand: "bg-brand-500/10 text-brand-600 dark:text-brand-300",
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
    sky: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
    violet: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35, ease: "easeOut" }}
      whileHover={{ y: -3 }}
      // min-w-0: see the same tile grid in SubscriptionPanel — without it, a
      // grid item won't shrink below an unbroken string's full width, and
      // `truncate` below never gets a chance to clip anything.
      className="card group relative min-w-0 overflow-hidden p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
        <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-transform group-hover:scale-110", tints[accent])}>
          <Icon size={15} />
        </span>
      </div>
      <p className="mt-2 truncate font-display text-lg font-bold leading-tight text-slate-900 dark:text-white">{value}</p>
    </motion.div>
  );
}

// ---- Overview --------------------------------------------------------------
function OverviewPanel({ data }) {
  const facts = [
    { icon: UserIcon, label: "Owner", value: data.owner?.name || "—", sub: data.owner?.email },
    { icon: Mail, label: "Billing email", value: data.billing_email || "—" },
    { icon: Hash, label: "Slug", value: data.slug },
    { icon: CalendarDays, label: "Created", value: fmtDate(data.created_at) },
  ];
  const usage = [
    { icon: FolderKanban, label: "Projects", value: data.projects ?? 0 },
    { icon: FileText, label: "Runs", value: data.runs ?? 0 },
    { icon: Bug, label: "Bugs", value: data.bugs ?? 0 },
    { icon: Users, label: "Members", value: data.total_users ?? data.seats ?? 0 },
  ];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Tile icon={Tag} label="Plan" value={data.plan_name || PLAN_NAME_FALLBACK[data.plan_id] || data.plan_id || "—"} accent="brand" delay={0} />
        <Tile icon={Users} label="Users" value={data.seats ?? 0} accent="sky" delay={0.04} />
        <Tile icon={FileText} label="Runs" value={data.runs ?? 0} accent="emerald" delay={0.08} />
        <Tile icon={CalendarDays} label="Start date" value={fmtDate(data.created_at)} accent="violet" delay={0.12} />
        <Tile icon={Clock} label="Trial ends" value={data.trial_end ? fmtDate(data.trial_end) : "—"} accent="amber" delay={0.16} />
        <Tile icon={Repeat} label="Subscription" value={inr(data.amount_paid)} accent="brand" delay={0.2} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Company details</h3>
          <dl className="mt-4 space-y-3">
            {facts.map((f) => (
              <div key={f.label} className="flex items-center gap-3">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300"><f.icon size={15} /></span>
                <dt className="w-28 shrink-0 text-xs font-medium uppercase tracking-wide text-slate-400">{f.label}</dt>
                <dd className="min-w-0 flex-1 text-right">
                  <p className={cn("truncate text-sm font-semibold text-slate-800 dark:text-white", f.cap && "capitalize")}>{f.value}</p>
                  {f.sub && <p className="truncate text-xs text-slate-400">{f.sub}</p>}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Usage</h3>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {usage.map((u) => (
              <div key={u.label} className="rounded-xl border border-slate-100 p-4 dark:border-white/5">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-300"><u.icon size={15} /></span>
                <p className="mt-2 font-display text-2xl font-bold text-slate-900 dark:text-white">{Number(u.value).toLocaleString()}</p>
                <p className="text-xs text-slate-400">{u.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Subscription ----------------------------------------------------------
function SubscriptionPanel({ data, onChanged }) {
  const sub = data.subscription || {};
  const [changeOpen, setChangeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [plan, setPlan] = useState(data.plan_id || "growth");
  const [busy, setBusy] = useState(false);
  const [buyer, setBuyer] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catalogErr, setCatalogErr] = useState(null);

  const canceled = String(data.status).toLowerCase() === "canceled";

  // Who actually paid — the subscription's user, which is not necessarily the
  // company owner. Supplementary: if the lookup fails the tile stays "—" rather
  // than naming someone the data doesn't support.
  useEffect(() => {
    let alive = true;
    getCompanySubscription(data.id)
      .then((d) => { if (alive) setBuyer(d?.buyer || null); })
      .catch(() => { if (alive) setBuyer(null); });
    return () => { alive = false; };
  }, [data.id]);

  // Prices for the Change Plan modal must come from the live, admin-editable
  // catalog — it is what change-plan bills, so a hardcoded list would show one
  // number and charge another.
  const loadCatalog = useCallback(async () => {
    setCatalogErr(null);
    try {
      setCatalog(await getPlans());
    } catch (e) {
      setCatalogErr((e && e.message) || "Couldn't load the plan catalog.");
    }
  }, []);
  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const planOptions = catalog
    ? Object.entries(catalog).map(([id, p]) => ({ id, name: p.name || PLAN_NAME_FALLBACK[id] || id, price: p.price_inr }))
    : [];

  async function doChange() {
    setBusy(true);
    try { await changeCompanyPlan(data.id, plan); }
    catch (e) { setBusy(false); toastError(e.message || "Couldn't change the plan."); return; }
    setBusy(false); setChangeOpen(false);
    toastSuccess(`Plan changed to ${planOptions.find((p) => p.id === plan)?.name || plan}.`);
    onChanged?.();
  }
  async function doCancel() {
    setBusy(true);
    try { await cancelCompanySubscription(data.id); }
    catch (e) { setBusy(false); toastError(e.message || "Couldn't cancel the subscription."); return; }
    setBusy(false); setCancelOpen(false);
    toastSuccess("Subscription canceled.");
    onChanged?.();
  }

  const tiles = [
    { icon: Tag, label: "Current Plan", value: data.plan_name || PLAN_NAME_FALLBACK[data.plan_id] || data.plan_id || "—", accent: "brand" },
    { icon: IndianRupee, label: "Plan Amount", value: inr(data.amount_paid), accent: "emerald" },
    { icon: Activity, label: "Status", value: <StatusBadge status={data.status} />, accent: "sky", raw: true },
    // No subscription row → no subscription dates. Falling back to the COMPANY's
    // created_at here reported a purchase that never happened.
    { icon: Clock, label: "Period Start", value: fmtDateTime(sub.created_at), accent: "violet" },
    { icon: CalendarDays, label: "Period end", value: fmtDate(data.current_period_end), accent: "amber" },
    {
      icon: UserIcon,
      label: "Last purchased",
      value: data.subscription ? (buyer?.name || "—") : "—",
      sub: data.subscription
        ? [buyer?.email, relTime(sub.updated_at || sub.created_at)].filter(Boolean).join(" · ")
        : "No purchase on record",
      accent: "brand",
      raw: true,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {tiles.map((t, i) => (
          <motion.div
            key={t.label}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04, duration: 0.35 }}
            whileHover={{ y: -3 }}
            // min-w-0: a grid item's default min-width is its content's
            // min-content size, and an unbroken string (a long name with no
            // spaces, say) has no break point to shrink at — so "Last
            // purchased" could grow the whole card past its column and spill
            // into the next one, with `truncate` below never even kicking in
            // since it only clips once the box is actually width-constrained.
            className="card group relative min-w-0 overflow-hidden p-4"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">{t.label}</p>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-500/10 text-brand-600 transition-transform group-hover:scale-110 dark:text-brand-300"><t.icon size={15} /></span>
            </div>
            <div className="mt-2 truncate font-display text-lg font-bold leading-tight text-slate-900 dark:text-white">{t.value}</div>
            {t.sub && <p className="mt-0.5 truncate text-xs text-slate-400">{t.sub}</p>}
          </motion.div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2.5">
        <Button variant="outline" size="sm" onClick={() => { setPlan(data.plan_id || "growth"); setChangeOpen(true); }}>
          <Repeat size={15} /> Change Plan
        </Button>
        <button
          type="button"
          disabled={canceled}
          onClick={() => setCancelOpen(true)}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3.5 text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-500/30 dark:bg-transparent dark:hover:bg-rose-500/10"
        >
          <Ban size={15} /> {canceled ? "Canceled" : "Cancel"}
        </button>
      </div>

      {/* change plan modal */}
      <Modal
        open={changeOpen}
        onClose={() => setChangeOpen(false)}
        title="Change plan"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setChangeOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={doChange} disabled={busy || !catalog}>{busy ? <Loader2 size={15} className="animate-spin" /> : "Apply plan"}</Button>
          </div>
        }
      >
        <p className="text-sm text-slate-500 dark:text-slate-400">Override the subscription plan for <span className="font-semibold text-slate-700 dark:text-slate-200">{data.name}</span>.</p>
        <LoadError message={catalogErr} onRetry={loadCatalog} className="mt-4" />
        {!catalog && !catalogErr ? (
          <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
        ) : (
          <div className="mt-4 space-y-2">
            {planOptions.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPlan(p.id)}
                className={cn(
                  "flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors",
                  plan === p.id ? "border-brand-400 bg-brand-50/60 dark:border-brand-500/40 dark:bg-brand-500/10" : "border-slate-200 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5"
                )}
              >
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white">{p.name}</p>
                  {/* a null catalog price is "contact us" — it is billed as 0 */}
                  <p className="text-xs text-slate-400">{p.price ? `${inr(p.price)} / mo` : "No charge"}</p>
                </div>
                <span className={cn("grid h-5 w-5 place-items-center rounded-full border-2", plan === p.id ? "border-brand-500 bg-brand-500 text-white" : "border-slate-300 dark:border-white/20")}>
                  {plan === p.id && <CheckCircle2 size={12} />}
                </span>
              </button>
            ))}
          </div>
        )}
      </Modal>

      {/* cancel confirm */}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel subscription"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setCancelOpen(false)}>Keep it</Button>
            <button type="button" onClick={doCancel} disabled={busy} className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-rose-600 px-3.5 text-sm font-semibold text-white hover:bg-rose-500 disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : "Cancel subscription"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">This marks the subscription <strong>canceled</strong>. Access continues until the current period ends. You can re-activate by changing the plan.</p>
      </Modal>
    </div>
  );
}

// ---- Invoices --------------------------------------------------------------
function InvoicesPanel({ id, demo }) {
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getCompanyInvoices(id)
      .then((d) => { if (alive) setInvoices(d); })
      .catch((e) => {
        if (!alive) return;
        // A failed request is NOT "this customer was never invoiced" — only a
        // successful empty response is.
        if (e && e.status === 0) setInvoices(demo ? DEMO_INVOICES : []);
        else { setInvoices(null); setError((e && e.message) || "Couldn't load invoices."); }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, demo, attempt]);

  if (loading) return <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>;
  if (error) return <LoadError message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  const rows = invoices || [];

  if (rows.length === 0) {
    return (
      <div className="card flex flex-col items-center justify-center py-16 text-center">
        <FileText size={30} className="mb-2 text-slate-300 dark:text-slate-600" />
        <p className="text-sm font-medium text-slate-600 dark:text-slate-300">No invoices yet</p>
        <p className="text-xs text-slate-400">Paid subscriptions generate invoices automatically.</p>
      </div>
    );
  }

  return (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50/80 dark:bg-white/5">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3">ID</th>
                <th className="px-5 py-3">Period</th>
                <th className="px-5 py-3">Amount</th>
                <th className="px-5 py-3">Refunded</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {rows.map((inv, i) => (
                <motion.tr
                  key={inv.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }}
                  className="hover:bg-slate-50 dark:hover:bg-white/5"
                >
                  <td className="px-5 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">{invoiceNumber(inv.id)}</td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                  <td className="px-5 py-3 font-semibold text-slate-800 dark:text-white">{inr(inv.amount)}</td>
                  <td className="px-5 py-3 text-sky-600 dark:text-sky-300">{inv.status === "refunded" ? inr(inv.amount) : "—"}</td>
                  <td className="px-5 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-5 py-3 text-slate-400">{relTime(inv.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1.5">
                      {/* No receipt button: printing the whole dashboard downloaded
                          nothing — a real receipt has to be served by the backend. */}
                      <button type="button" title="View" onClick={() => setView(inv)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-brand-300 hover:text-brand-600 dark:border-white/10 dark:text-slate-300">
                        <Eye size={14} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-200 px-5 py-2.5 text-xs text-slate-400 dark:border-white/10">{rows.length} invoice{rows.length > 1 ? "s" : ""}</div>
      </div>

      <Modal
        open={!!view}
        onClose={() => setView(null)}
        title="Invoice"
        size="md"
        footer={
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={() => setView(null)}>Close</Button>
            {/* Same mechanism as the tester/admin's own invoice modal — a real
                downloadable file needs a backend-generated PDF, which doesn't
                exist yet, but the browser's own print-to-PDF (scoped to just
                this content via the shared `.invoice-print` CSS rule) already
                gives a genuine "download" today. */}
            <Button size="sm" onClick={() => window.print()} className="gap-2">
              <Printer size={15} /> Print / Download
            </Button>
          </div>
        }
      >
        {view && (
          <div className="invoice-print space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-sm text-slate-500 dark:text-slate-400">Invoice #{invoiceNumber(view.id)}</p>
                <p className="mt-1 font-display text-2xl font-bold text-slate-900 dark:text-white">{inr(view.amount)}</p>
              </div>
              <StatusBadge status={view.status} />
            </div>
            <dl className="divide-y divide-slate-100 rounded-xl border border-slate-100 text-sm dark:divide-white/5 dark:border-white/10">
              {[
                ["Plan", view.plan_name || PLAN_NAME_FALLBACK[view.plan_id] || view.plan_id],
                ["Period", `${fmtDate(view.period_start)} – ${fmtDate(view.period_end)}`],
                ["Currency", view.currency || "INR"],
                ["Payment ID", view.razorpay_payment_id || "—"],
                ["Created", fmtDateTime(view.created_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-4 py-2.5">
                  <dt className="text-slate-400">{k}</dt>
                  <dd className="font-medium text-slate-700 dark:text-slate-200">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </Modal>
    </>
  );
}

// ---- Notes (local) ---------------------------------------------------------
function NotesPanel({ id }) {
  const KEY = `aiqa-company-notes-${id}`;
  const [notes, setNotes] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch (e) { return []; } });
  const [draft, setDraft] = useState("");

  // Write first, then show: a quota-exceeded write used to be swallowed, leaving
  // the note on screen and permanently unsaved.
  const persist = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      toastError("Couldn't save the note — this browser's local storage is full.");
      return false;
    }
    setNotes(list);
    return true;
  };
  const add = () => {
    const text = draft.trim();
    if (!text) return;
    if (persist([{ id: String(Date.now()), text, at: new Date().toISOString() }, ...notes])) {
      setDraft("");
      toastSuccess("Note added.");
    }
  };
  const remove = (nid) => {
    if (persist(notes.filter((n) => n.id !== nid))) toastSuccess("Note deleted.");
  };

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {/* sticky: the notes list can run much longer than this form, and a grid
          cell doesn't stretch a shorter sibling to fill it — it just leaves
          the rest of the column's height blank. Keeping the form in view
          while the list scrolls past it puts that space to use instead of
          just leaving it empty. */}
      <div className="card h-fit p-5 lg:sticky lg:top-20 lg:col-span-1">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white"><StickyNote size={16} /> Add a note</h3>
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <ShieldAlert size={14} className="mt-0.5 shrink-0" />
          <span>Saved in this browser only — no other operator can see these notes, and clearing site data deletes them.</span>
        </div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") add(); }}
          rows={4}
          maxLength={NOTE_MAX_LEN}
          placeholder="Add context about this tenant…"
          className="mt-3 w-full resize-none rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-500/10 dark:border-white/10 dark:bg-white/5 dark:text-white"
        />
        {/* The 2000-char cap was already enforced (the field just silently stops
            accepting input), but nothing ever said so — it read as no limit at
            all until you happened to hit it. */}
        <p className={cn(
          "mt-1 text-right text-xs",
          draft.length >= NOTE_MAX_LEN ? "font-semibold text-rose-500" : "text-slate-400"
        )}>
          {draft.length} / {NOTE_MAX_LEN}
        </p>
        <Button size="sm" className="mt-2 w-full" onClick={add} disabled={!draft.trim()}><Plus size={15} /> Add note</Button>
      </div>

      <div className="lg:col-span-2">
        {notes.length === 0 ? (
          <div className="card flex flex-col items-center justify-center py-16 text-center">
            <StickyNote size={30} className="mb-2 text-slate-300 dark:text-slate-600" />
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">No notes yet</p>
            <p className="text-xs text-slate-400">Jot down anything worth remembering.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence initial={false}>
              {notes.map((n) => (
                <motion.div
                  key={n.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="card flex items-start gap-3 p-4"
                >
                  <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-300"><StickyNote size={15} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="whitespace-pre-wrap break-words text-sm text-slate-700 dark:text-slate-200">{n.text}</p>
                    <p className="mt-1 text-xs text-slate-400">{relTime(n.at)} · {fmtDateTime(n.at)}</p>
                  </div>
                  <button type="button" onClick={() => remove(n.id)} title="Delete" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10">
                    <Trash2 size={14} />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Danger Zone -----------------------------------------------------------
function DangerPanel({ data, onChanged, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [softOpen, setSoftOpen] = useState(false);
  const [hardOpen, setHardOpen] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [hardErr, setHardErr] = useState(null);

  const suspended = !data.is_active;

  // POST /suspend FLIPS the flag, so the buttons have to stay disabled until the
  // refetched company is on screen — otherwise a second click on what still says
  // "Suspend" quietly reactivates the tenant.
  async function doSuspend() {
    if (busy) return;
    setBusy(true);
    // Read BEFORE the request — onChanged() below refetches `data`, and by
    // then `suspended` (derived from it) may already reflect the new state,
    // which would report the action backwards.
    const wasSuspended = suspended;
    try {
      await suspendCompany(data.id);
      setSuspendOpen(false);
      toastSuccess(wasSuspended ? "Company reactivated." : "Company suspended.");
      await onChanged?.();
    } catch (e) {
      toastError(e.message || "Couldn't update the company.");
    } finally {
      setBusy(false);
    }
  }
  async function doSoftDelete() {
    if (busy) return;
    setBusy(true);
    // Soft delete only deactivates — stay on the page (it flips to "suspended")
    // so a hard delete is still available right here afterwards.
    try {
      await softDeleteCompany(data.id);
      setSoftOpen(false);
      // Distinct from Suspend's message on purpose (#45) — the two buttons
      // both flip is_active, but only this one also cancels the subscription
      // (see admin_soft_delete_company's own docstring), and with no visible
      // feedback at all before, that difference was invisible either way.
      toastSuccess("Company deactivated and its subscription canceled.");
      await onChanged?.();
    } catch (e) {
      toastError(e.message || "Couldn't deactivate the company.");
    } finally {
      setBusy(false);
    }
  }
  const hardConfirmed = pwInput && slugInput === data.slug;

  async function doHardDelete() {
    if (busy || !hardConfirmed) return;
    setBusy(true); setHardErr(null);
    try {
      await hardDeleteCompany(data.id, pwInput);
      toastSuccess("Company permanently deleted.");
      onDeleted?.();
    } catch (e) { setHardErr(e?.message || "Delete failed."); setBusy(false); }
  }

  const rows = [
    {
      title: suspended ? "Reactivate company" : "Suspend company",
      // No reason is collected or stored anywhere, so don't claim one is.
      desc: suspended ? "Restore access for this tenant." : "Block all logins for this tenant.",
      btn: suspended ? <><CheckCircle2 size={15} /> Reactivate</> : <><Ban size={15} /> Suspend</>,
      tone: suspended ? "emerald" : "rose",
      onClick: () => setSuspendOpen(true),
    },
    {
      title: "Delete company (soft)",
      // Spelled out because it otherwise reads as a duplicate of Suspend just
      // above — both block access, but only this one also cancels their
      // subscription (#45). Slug confirmation makes it harder to fire by
      // mistake than a plain "are you sure".
      desc: "Block access AND cancel the subscription. Slug must be typed to confirm. Reversible.",
      btn: <><Trash2 size={15} /> Delete</>,
      tone: "rose",
      onClick: () => { setSlugInput(""); setSoftOpen(true); },
    },
    {
      title: "Delete company (hard)",
      desc: "Permanently remove all data. Slug and superadmin password required to confirm.",
      btn: <><ShieldAlert size={15} /> Delete Permanently</>,
      tone: "rose",
      onClick: () => { setSlugInput(""); setPwInput(""); setHardErr(null); setHardOpen(true); },
    },
  ];

  return (
    <div className="card overflow-hidden border-rose-200 dark:border-rose-500/20">
      <div className="border-b border-rose-100 bg-rose-50/50 px-5 py-4 dark:border-rose-500/10 dark:bg-rose-500/5">
        <h3 className="flex items-center gap-2 text-sm font-bold text-rose-600 dark:text-rose-400"><ShieldAlert size={16} /> Danger zone</h3>
        <p className="mt-0.5 text-xs text-rose-500/80 dark:text-rose-300/70">These actions affect access and data — proceed carefully.</p>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-white/5">
        {rows.map((r) => (
          <div key={r.title} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">{r.title}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{r.desc}</p>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={r.onClick}
              className={cn(
                "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-xl px-3.5 text-sm font-semibold text-white transition-colors disabled:opacity-60",
                r.tone === "emerald" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"
              )}
            >
              {r.btn}
            </button>
          </div>
        ))}
      </div>

      {/* suspend / reactivate confirm */}
      <Modal
        open={suspendOpen}
        onClose={() => setSuspendOpen(false)}
        title={suspended ? "Reactivate company" : "Suspend company"}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSuspendOpen(false)}>Cancel</Button>
            <button
              type="button"
              disabled={busy}
              onClick={doSuspend}
              className={cn(
                "inline-flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                suspended ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"
              )}
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : (suspended ? <><CheckCircle2 size={15} /> Reactivate</> : <><Ban size={15} /> Suspend</>)}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {suspended
            ? <>Restore access for <strong>{data.name}</strong>? Its members will be able to use the product again.</>
            : <>Block access for <strong>{data.name}</strong>? Every member is locked out immediately — including sessions that are already signed in.</>}
        </p>
      </Modal>

      {/* soft delete */}
      <Modal
        open={softOpen}
        onClose={() => setSoftOpen(false)}
        title="Delete company (soft)"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSoftOpen(false)}>Cancel</Button>
            <button type="button" disabled={busy || slugInput !== data.slug} onClick={doSoftDelete} className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-rose-600 px-3.5 text-sm font-semibold text-white hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <><Trash2 size={15} /> Soft delete</>}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">This deactivates <strong>{data.name}</strong> and all its members. Type the slug <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-rose-600 dark:bg-white/10">{data.slug}</code> to confirm.</p>
        <input aria-label="Type the company slug to confirm" value={slugInput} onChange={(e) => setSlugInput(e.target.value)} placeholder={data.slug} className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:border-rose-400 focus:bg-white focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white" />
      </Modal>

      {/* hard delete */}
      <Modal
        open={hardOpen}
        onClose={() => setHardOpen(false)}
        title="Permanently delete company"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setHardOpen(false)}>Cancel</Button>
            <button type="button" disabled={busy || !hardConfirmed} onClick={doHardDelete} className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-rose-600 px-3.5 text-sm font-semibold text-white hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <><ShieldAlert size={15} /> Delete everything</>}
            </button>
          </div>
        }
      >
        <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-3 text-xs text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
          This <strong>cannot be undone</strong>. All projects, runs, bugs, members, subscriptions and invoices for <strong>{data.name}</strong> are erased.
        </div>
        {/* The irreversible delete must ask for at least as much confirmation as the
            reversible one above it — hence the slug, not just a password. */}
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">Type the slug <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-rose-600 dark:bg-white/10">{data.slug}</code> to confirm:</p>
        <input autoFocus aria-label="Type the company slug to confirm" value={slugInput} onChange={(e) => setSlugInput(e.target.value)} placeholder={data.slug} className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:border-rose-400 focus:bg-white focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white" />
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">Enter your superadmin password to confirm:</p>
        <input type="password" aria-label="Your account password" autoComplete="new-password" value={pwInput} onChange={(e) => setPwInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && hardConfirmed && !busy) doHardDelete(); }} placeholder="••••••••" className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:border-rose-400 focus:bg-white focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white" />
        {hardErr && <p className="mt-2 text-sm font-medium text-rose-600">{hardErr}</p>}
      </Modal>
    </div>
  );
}
